import { Fragment, useEffect, useState } from "react";
import { FaPlusSquare } from "react-icons/fa";
import { toast } from "react-toastify";
import BillingOrgModal from "@elements/modals/BillingOrgModal";
import {
    companyBillingConfigStatusClass,
    companyBillingConfigStatusLabel,
    loadBillingOrgDetailAsync,
    loadBillingOrgsDetailedAsync,
    type BillingOrgDetail,
    type BillingOrgDetails,
    type CompanyBillingConfiguration,
} from "@/utils/billingOrgHelpers";

type BillingOrgsTabProps = {
    isActive: boolean;
    updateSpinner?: (loading: boolean) => void;
    refreshKey?: number;
};

function emptyBillingOrg(): BillingOrgDetails {
    return {
        id: 0,
        name: "",
        legalName: "",
        remitToAddress: "",
        remitToEmail: "",
        taxId: "",
        directorUserId: null,
    };
}

export default function BillingOrgsTab({ isActive, updateSpinner, refreshKey }: BillingOrgsTabProps) {
    const [orgs, setOrgs] = useState<BillingOrgDetails[]>([]);
    const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
    const [detail, setDetail] = useState<BillingOrgDetail | null>(null);
    const [expandedCompanyId, setExpandedCompanyId] = useState<number | null>(null);
    const [editOrg, setEditOrg] = useState<BillingOrgDetails | null>(null);
    const [loading, setLoading] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (isActive) fetchOrgs();
    }, [isActive, refreshKey]);

    useEffect(() => {
        if (!isActive || !selectedOrgId) {
            setDetail(null);
            return;
        }
        fetchDetail(selectedOrgId);
    }, [isActive, selectedOrgId, refreshKey]);

    async function fetchOrgs() {
        setLoading(true);
        setError(null);
        updateSpinner?.(true);
        try {
            const data = await loadBillingOrgsDetailedAsync();
            setOrgs(data);
            if (data.length === 0) {
                setSelectedOrgId(null);
            } else if (!selectedOrgId || !data.some((org) => org.id === selectedOrgId)) {
                setSelectedOrgId(data[0].id);
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : "Unknown error";
            setError(message);
            toast.error("Failed to load billing orgs.");
        } finally {
            setLoading(false);
            updateSpinner?.(false);
        }
    }

    async function fetchDetail(orgId: number) {
        setDetailLoading(true);
        try {
            const data = await loadBillingOrgDetailAsync(orgId);
            setDetail(data);
            setExpandedCompanyId(null);
            if (data?.org) {
                setOrgs((prev) =>
                    prev.map((org) =>
                        org.id === orgId ? { ...org, companyCount: data.org.companyCount } : org
                    )
                );
            }
        } catch {
            toast.error("Failed to load billing org details.");
            setDetail(null);
        } finally {
            setDetailLoading(false);
        }
    }

    function handleCloseModal() {
        setEditOrg(null);
        fetchOrgs();
        if (selectedOrgId) fetchDetail(selectedOrgId);
    }

    function handleOrgClick(org: BillingOrgDetails) {
        if (selectedOrgId !== org.id) {
            setSelectedOrgId(org.id);
            return;
        }
        setEditOrg(org);
    }

    function handleAddOrg() {
        setEditOrg(emptyBillingOrg());
    }

    function toggleCompanyIssues(company: CompanyBillingConfiguration) {
        if (company.issues.length === 0) return;
        setExpandedCompanyId((prev) => (prev === company.companyId ? null : company.companyId));
    }

    const selectedOrg = orgs.find((org) => org.id === selectedOrgId) ?? null;

    return (
        <>
            <div className="horizontal">
                <div className="tableWrapper dimensionTable scrollable">
                    <table>
                        <caption>
                            <span className="title">
                                Billing Orgs
                                {!loading && (
                                    <>
                                        <span className="captionCount">({orgs.length})</span>
                                        <span className="captionRight">
                                            <FaPlusSquare
                                                className="actionIcon"
                                                title="Add billing org"
                                                onClick={handleAddOrg}
                                            />
                                        </span>
                                    </>
                                )}
                            </span>
                        </caption>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Director</th>
                                <th title="Active companies assigned to this billing org">Active cos.</th>
                                <th title="RightTime users granted access to this org's accounting views (dashboard, invoices, payments) without System Admin">Accounting access</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={4}>Loading…</td></tr>
                            ) : error ? (
                                <tr><td colSpan={4}>{error}</td></tr>
                            ) : orgs.length > 0 ? (
                                orgs.map((org) => (
                                    <tr
                                        key={org.id}
                                        className={`clickableRow${selectedOrgId === org.id ? " active" : ""}`}
                                        onClick={() => handleOrgClick(org)}
                                        title={selectedOrgId === org.id ? "Click again to edit" : "Select billing org"}
                                    >
                                        <td>{org.name}</td>
                                        <td>{org.directorName || "—"}</td>
                                        <td>{org.companyCount ?? 0}</td>
                                        <td>{org.assignedUserIds?.length ?? 0}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan={4} className="center">No billing orgs found.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="tableWrapper factTable scrollable">
                    <table>
                        <caption>
                            <span className="title">
                                Company Assignments
                                {selectedOrg ? ` — ${selectedOrg.name}` : ""}
                                {detail ? <span className="captionCount">({detail.companies.length})</span> : null}
                            </span>
                        </caption>
                        <thead>
                            <tr>
                                <th>Company</th>
                                <th>Active</th>
                                <th>Billable</th>
                                <th>Status</th>
                                <th>Issues</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!selectedOrg ? (
                                <tr><td colSpan={5} className="center">Select a billing org to view company assignments.</td></tr>
                            ) : detailLoading ? (
                                <tr><td colSpan={5} className="center">Loading company assignments…</td></tr>
                            ) : !detail ? (
                                <tr><td colSpan={5} className="center">Unable to load billing org details.</td></tr>
                            ) : detail.companies.length > 0 ? (
                                detail.companies.map((company) => (
                                    <Fragment key={company.companyId}>
                                        <tr
                                            className={company.issues.length > 0 ? "clickableRow" : undefined}
                                            onClick={() => toggleCompanyIssues(company)}
                                            title={company.issues.length > 0 ? "Click to show configuration issues" : undefined}
                                        >
                                            <td>{company.companyName}</td>
                                            <td>{company.active ? "Yes" : "No"}</td>
                                            <td>{company.billable ? "Yes" : "No"}</td>
                                            <td>
                                                <span className={companyBillingConfigStatusClass(company)}>
                                                    {companyBillingConfigStatusLabel(company)}
                                                </span>
                                            </td>
                                            <td>
                                                {company.errorCount > 0 && (
                                                    <span className="billingConfigError">{company.errorCount} error(s)</span>
                                                )}
                                                {company.errorCount > 0 && company.warningCount > 0 ? ", " : null}
                                                {company.warningCount > 0 && (
                                                    <span className="billingConfigWarning">{company.warningCount} warning(s)</span>
                                                )}
                                                {company.errorCount === 0 && company.warningCount === 0 && company.issues.length === 0
                                                    ? "—"
                                                    : null}
                                            </td>
                                        </tr>
                                        {expandedCompanyId === company.companyId && company.issues.length > 0 && (
                                            <tr className="billingOrgsIssueRow">
                                                <td colSpan={5}>
                                                    <ul className="billingOrgsIssueList">
                                                        {company.issues.map((issue) => (
                                                            <li
                                                                key={`${issue.code}-${issue.companyTestTypeId ?? "company"}`}
                                                                className={
                                                                    issue.severity === "Error"
                                                                        ? "billingConfigError"
                                                                        : issue.severity === "Warning"
                                                                          ? "billingConfigWarning"
                                                                          : "billingConfigSkipped"
                                                                }
                                                            >
                                                                {issue.message}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                ))
                            ) : (
                                <tr><td colSpan={5} className="center">No companies assigned to this billing org.</td></tr>
                            )}
                        </tbody>
                    </table>
                    {detail && !detailLoading && (
                        <div className="billingOrgsSummary">
                            <span>{detail.activeCompanyCount} active, {detail.inactiveCompanyCount} inactive</span>
                            <span className="billingConfigOk">{detail.configuredCompanyCount} configured</span>
                            <span className="billingConfigError">{detail.misconfiguredCompanyCount} need attention</span>
                        </div>
                    )}
                </div>
            </div>

            <BillingOrgModal
                isOpen={editOrg !== null}
                org={editOrg}
                onClose={handleCloseModal}
                updateSpinner={updateSpinner}
            />
        </>
    );
}
